import React, { useMemo, useState } from "react";
import { FilePreview } from "@/components/FilePreview/FilePreview";
import { fileUploadService } from "@/app/providers/services";
import type { FileAttachment } from "@packages/commons";
import LoadingOverlay from "@imminently/decisively-ui/LoadingOverlay";
import EmptyOverlay from "@imminently/decisively-ui/EmptyOverlay";
import ItemList from "@imminently/decisively-ui/ItemList";
import SplitPane from "@imminently/decisively-ui/SplitPane";

interface FilesTabProps {
  sessionId: string;
  versionId?: string;
  sessionAttachments?: FileAttachment[];
  interactionAttachments?: FileAttachment[];
}

export const FilesTab = ({
  sessionId,
  versionId,
  sessionAttachments = [],
  interactionAttachments = [],
}: FilesTabProps) => {
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [loadingFiles, setLoadingFiles] = useState<Set<string>>(new Set());
  const [fileDataCache, setFileDataCache] = useState<Map<string, string>>(new Map());

  // Combine session and interaction attachments, avoiding duplicates by file name
  const allAttachments = useMemo(() => {
    const attachments: FileAttachment[] = [...sessionAttachments];
    const seenNames = new Set(sessionAttachments.map((attachment) => attachment.name));
    interactionAttachments.forEach((attachment) => {
      if (!seenNames.has(attachment.name)) {
        attachments.push(attachment);
        seenNames.add(attachment.name);
      }
    });
    return attachments;
  }, [sessionAttachments, interactionAttachments]);


  const loadFileData = React.useCallback(async (attachment: FileAttachment): Promise<string | null> => {
    // If we already have the data, return it
    if (attachment.data) {
      return attachment.data;
    }

    // If we have a reference, fetch it
    if (attachment.reference) {
      const cacheKey = attachment.reference;
      if (fileDataCache.has(cacheKey)) {
        return fileDataCache.get(cacheKey)!;
      }

      setLoadingFiles((prev) => new Set(prev).add(attachment.name));
      try {
        // Extract file ID from reference (data:id=<uuid>;base64,<name>)
        const match = attachment.reference.match(/^data:id=([^;]+);base64,/);
        if (!match) {
          throw new Error("Invalid file reference");
        }
        const fileId = match[1];

        // Fetch file using file service
        const result = await fileUploadService.getOne(fileId);

        // Convert to data URL format
        // result.data contains the file data, result.name is the filename
        let fileData = result.data;
        if (typeof fileData === "object" && fileData !== null) {
          // If it's an object, try to get the data property
          fileData = (fileData as any).data || fileData;
        }

        // If fileData is already a data URL, use it directly
        if (typeof fileData === "string" && fileData.startsWith("data:")) {
          setFileDataCache((prev) => new Map(prev).set(cacheKey, fileData));
          return fileData;
        }

        // Otherwise, construct a data URL from the file data
        if (typeof fileData === "string") {
          // Extract MIME type if available, otherwise use a default
          const mimeType = "application/octet-stream";
          const dataUrl = `data:${mimeType};base64,${fileData}`;
          setFileDataCache((prev) => new Map(prev).set(cacheKey, dataUrl));
          return dataUrl;
        }
      } catch (error) {
        console.error("Error loading file:", error);
      } finally {
        setLoadingFiles((prev) => {
          const next = new Set(prev);
          next.delete(attachment.name);
          return next;
        });
      }
    }

    return null;
  }, [fileDataCache]);

  const handleFileSelect = React.useCallback(async (attachment: FileAttachment) => {
    // Set selected file immediately to show loading state
    setSelectedFile(attachment.name);
    // Load file data if needed
    await loadFileData(attachment);
  }, [loadFileData]);

  // Auto-select first file when attachments are available
  React.useEffect(() => {
    if (allAttachments.length > 0 && !selectedFile) {
      const firstAttachment = allAttachments[0];
      handleFileSelect(firstAttachment);
    }
  }, [allAttachments.length, selectedFile, handleFileSelect]);

  const selectedAttachment = selectedFile
    ? allAttachments.find((attachment) => attachment.name === selectedFile)
    : null;
  const selectedFileData = selectedAttachment
    ? (selectedAttachment.data || fileDataCache.get(selectedAttachment.reference || ""))
    : null;
  const isSelectedFileLoading = selectedFile && loadingFiles.has(selectedFile);

  if (allAttachments.length === 0) {
    return (
      <div className="flex items-center justify-center h-full relative">
        <EmptyOverlay message="No files attached to this session" />
      </div>
    );
  }

  return (
    <SplitPane direction="horizontal" gutterSize={6}>
      <SplitPane.Pane minSize={200} preferredSize="256px">
        <ItemList
          className="h-full"
          header="Files"
          items={allAttachments}
          getItemKey={(attachment, idx) => attachment.reference || attachment.name || String(idx)}
          renderItem={(attachment) => {
            const isSelected = selectedFile === attachment.name;

            return (
              <ItemList.Item
                key={attachment.reference || attachment.name}
                selected={isSelected}
                onClick={() => handleFileSelect(attachment)}
              >
                <ItemList.Left>
                  <div className="flex flex-col overflow-hidden">
                    <ItemList.Title>{attachment.name}</ItemList.Title>
                    {attachment.label ? (
                      <div className="text-[10px] text-textSecondary truncate">
                        {attachment.label}
                      </div>
                    ) : null}
                  </div>
                </ItemList.Left>
              </ItemList.Item>
            );
          }}
        />
      </SplitPane.Pane>

      <SplitPane.Pane>
        <div className="h-full flex flex-col overflow-hidden">
          {selectedFile ? (
            isSelectedFileLoading ? (
              <div className="flex-1 relative">
                <LoadingOverlay message="Loading file..." />
              </div>
            ) : selectedFileData ? (
              <FilePreview
                key={selectedFile}
                attachment={{ ...selectedAttachment!, data: selectedFileData }}
                className="h-full"
              />
            ) : (
              <div className="flex-1 relative">
                <EmptyOverlay message="Unable to load file data" />
              </div>
            )
          ) : (
            <div className="flex-1 relative">
              <EmptyOverlay message="Select a file to preview" />
            </div>
          )}
        </div>
      </SplitPane.Pane>
    </SplitPane>
  );
};
